import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ListItem } from '@travel-planner/shared';
import { useTripItems } from '../lists/useListItems';

export interface RoutePoint {
  lng: number;
  lat: number;
  name: string;
}

interface MapContextValue {
  tripId: string;
  items: ListItem[]; // all trip items
  placedItems: ListItem[]; // items with a location (the markers)
  focusId: string | null;
  focus: (itemId: string) => void;
  clearFocus: () => void;
  // Placing an existing (unplaced) item: next map click sets its location.
  placingItemId: string | null;
  startPlacing: (itemId: string) => void;
  cancelPlacing: () => void;
  route: RoutePoint[] | null;
  routeLabel: string | null;
  showRoute: (label: string, points: RoutePoint[]) => void;
  clearRoute: () => void;
}

const MapContext = createContext<MapContextValue | null>(null);

export function MapProvider({ tripId, children }: { tripId: string; children: ReactNode }) {
  const { data: items = [] } = useTripItems(tripId);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [placingItemId, setPlacingItemId] = useState<string | null>(null);
  const [route, setRoute] = useState<RoutePoint[] | null>(null);
  const [routeLabel, setRouteLabel] = useState<string | null>(null);

  const placedItems = useMemo(
    () => items.filter((i) => i.lat !== null && i.lng !== null),
    [items],
  );

  const value: MapContextValue = {
    tripId,
    items,
    placedItems,
    focusId,
    focus: (id) => {
      setFocusId(id);
      setPlacingItemId(null);
    },
    clearFocus: () => setFocusId(null),
    placingItemId,
    startPlacing: setPlacingItemId,
    cancelPlacing: () => setPlacingItemId(null),
    route,
    routeLabel,
    showRoute: (label, points) => {
      setRouteLabel(label);
      setRoute(points);
    },
    clearRoute: () => {
      setRoute(null);
      setRouteLabel(null);
    },
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

export function useMapContext(): MapContextValue {
  const ctx = useContext(MapContext);
  if (!ctx) throw new Error('useMapContext must be used within a MapProvider');
  return ctx;
}
